'use client';

import MenuIcon from '@mui/icons-material/Menu';
import type { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import MuiAppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import type { MouseEvent } from 'react';
import { useState } from 'react';

import DownloadFilePopover from '@/app/components/CommonLayout/DownloadFilePopover';
import { stringAvatar } from '@/app/components/Provider/ThemeProvider/avatar';
import { SIDEBAR } from '@/app/components/Provider/ThemeProvider/constant';
import { bgBlur } from '@/app/components/Provider/ThemeProvider/css';
import { MenuInfo } from '@/constants/Menu';
import { useDispatch, useSelector } from '@/stores';
import { authActions } from '@/stores/authSlice';
import { historyActions } from '@/stores/historySlice';
import { uiActions } from '@/stores/uiSlice';

const Header = () => {
  const theme = useTheme();
  const pathname = usePathname();
  const router = useRouter();
  const uiState = useSelector((state) => state.ui);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState<HTMLButtonElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = (nextUrl?: string): void => {
    dispatch(authActions.clearUser());
    dispatch(historyActions.clearHistory());
    queryClient.clear();
    router.push(nextUrl ? `/login/?nextUrl=${nextUrl}` : '/login');
  };

  const currentPageName = (() => {
    const foundMenu = Object.values(MenuInfo).find((menu) => {
      if (menu.sub) {
        const foundSub = Object.values(menu.sub).find((sub) => {
          return sub.path === pathname;
        });
        if (foundSub) return true;
      }
      return menu.path === pathname;
    });
    if (!foundMenu) return '';
    if (!foundMenu?.sub) return foundMenu.label;

    return Object.values(foundMenu.sub).find((item) => item.path === pathname)?.label ?? '';
  })();

  return (
    <AppBar
      sx={{
        ...bgBlur({
          color: theme.palette.background.default,
        }),
      }}
      open={uiState.openDrawer}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        <IconButton
          size="large"
          edge="start"
          sx={{ mr: 2 }}
          onClick={() => dispatch(uiActions.toggleDrawer(!uiState.openDrawer))}
          color="primary"
        >
          <MenuIcon />
        </IconButton>
        {!uiState.openDrawer && (
          <Typography variant="h4" sx={{ color: (theme) => theme.palette.grey[800] }}>
            {currentPageName}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={1}>
          <DownloadFilePopover />
          <>
            <IconButton
              onClick={handleOpen}
              sx={{
                width: 40,
                height: 40,
                background: (theme) =>
                  open
                    ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
                    : alpha(theme.palette.grey[500], 0.08),
              }}
            >
              <Avatar
                alt={user?.user.name}
                {...stringAvatar(user?.employee.name ?? '', {
                  width: 36,
                  height: 36,
                  border: (theme) => `solid 2px ${theme.palette.background.default}`,
                })}
              />
            </IconButton>

            <Popover
              open={!!open}
              anchorEl={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    p: 0,
                    mt: 1,
                    ml: 0.75,
                    width: 200,
                  },
                },
              }}
            >
              <Box sx={{ my: 1.5, px: 2 }}>
                <Typography variant="subtitle2" noWrap>
                  {user?.employee.name ?? ''}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                  {user?.user.role_name ?? ''}
                </Typography>
              </Box>

              <Divider sx={{ borderStyle: 'dashed', m: 0 }} />

              <MenuItem
                disableRipple
                disableTouchRipple
                onClick={() => handleLogout(pathname.slice(1))}
                sx={{ typography: 'body2', color: 'error.main', py: 1.5 }}
              >
                Đăng xuất
              </MenuItem>
            </Popover>
          </>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Header;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: SIDEBAR.WIDTH,
    width: `calc(100% - ${SIDEBAR.WIDTH}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));
